import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import TransitionOverlay from "@/components/TransitionOverlay";
import { useToast } from "@/hooks/use-toast";
import {
  getAuthErrorMessage,
  resetPassword,
  signInWithEmail,
  signInWithGoogle,
  signInWithMicrosoft,
  signUpWithEmail,
  validateEmail,
  validatePassword,
} from "@/lib/auth";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

type AuthMode = "signIn" | "signUp" | "forgotPassword";

export default function Login() {
  const [searchParams] = useSearchParams();
  const initialMode: AuthMode = searchParams.get("mode") === "signup" ? "signUp" : "signIn";

  const [authMode, setAuthMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const isSignUp = authMode === "signUp";
  const isForgotPassword = authMode === "forgotPassword";

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    if (!validateEmail(email)) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    // Additional validation for sign up
    if (isSignUp) {
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        toast({
          title: "Error",
          description: passwordValidation.message,
          variant: "destructive",
        });
        return;
      }

      if (password !== confirmPassword) {
        toast({
          title: "Error",
          description: "Passwords do not match",
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);
    try {
      if (isSignUp) {
        await signUpWithEmail(email, password);
        // Toast moved to App.tsx - shown only after user lands on main app
      } else {
        await signInWithEmail(email, password);
      }
    } catch (error: any) {
      toast({
        title: "Authentication Error",
        description: getAuthErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    if (!validateEmail(email)) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email);
      toast({
        title: "Password Reset Email Sent",
        description: "Check your inbox for instructions to reset your password.",
        duration: 5000,
      });
      setAuthMode("signIn");
    } catch (error: any) {
      toast({
        title: "Error",
        description: getAuthErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFederatedAuth = async (provider: "google" | "microsoft") => {
    setLoading(true);
    try {
      switch (provider) {
        case "google":
          await signInWithGoogle();
          break;
        case "microsoft":
          await signInWithMicrosoft();
          break;
      }
    } catch (error: any) {
      toast({
        title: "Authentication Error",
        description: getAuthErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Determine loading overlay text based on auth mode
  const getLoadingTitle = () => {
    if (isForgotPassword) return "Sending reset link...";
    return isSignUp ? "Creating account..." : "Signing in...";
  };

  const getLoadingSubtitle = () => {
    if (isForgotPassword) return "Please wait...";
    return isSignUp ? "Setting up your account..." : "Verifying credentials...";
  };

  return (
    <>
      <TransitionOverlay
        open={loading}
        title={getLoadingTitle()}
        subtitle={getLoadingSubtitle()}
      />
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-foreground">
            {isForgotPassword
              ? "Reset your password"
              : isSignUp
                ? "Create your account"
                : "Sign in to your account"}
          </h2>
          {!isForgotPassword && (
            <p className="mt-2 text-sm text-muted-foreground">
              {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
              <button
                type="button"
                className="font-medium text-primary hover:text-primary/80"
                onClick={() => {
                  setAuthMode(isSignUp ? "signIn" : "signUp");
                  setConfirmPassword("");
                }}>
                {isSignUp ? "Sign in" : "Sign up"}
              </button>
            </p>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              {isForgotPassword ? "Reset Password" : isSignUp ? "Create Account" : "Sign In"}
            </CardTitle>
            <CardDescription className="text-center">
              {isForgotPassword
                ? "Enter your email and we'll send you a link to reset your password"
                : isSignUp
                  ? "Enter your details to create a new account"
                  : "Enter your credentials to access your account"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Forgot Password Form */}
            {isForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    className="text-sm font-medium text-primary hover:text-primary/80"
                    onClick={() => setAuthMode("signIn")}>
                    Back to Sign In
                  </button>
                </div>
              </form>
            ) : (
              <>
                {/* Federated Sign-in Buttons */}
                <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => handleFederatedAuth("google")}
                disabled={loading}>
                <svg
                  className="w-5 h-5 mr-2"
                  viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => handleFederatedAuth("microsoft")}
                disabled={loading}>
                <svg
                  className="w-5 h-5 mr-2"
                  viewBox="0 0 21 21">
                  <rect x="0" y="0" width="10" height="10" fill="#F25022" />
                  <rect x="11" y="0" width="10" height="10" fill="#00A4EF" />
                  <rect x="0" y="11" width="10" height="10" fill="#7FBA00" />
                  <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                </svg>
                Continue with Microsoft
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form
              onSubmit={handleEmailAuth}
              className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {!isSignUp && (
                    <button
                      type="button"
                      className="text-sm font-medium text-primary hover:text-primary/80"
                      onClick={() => setAuthMode("forgotPassword")}>
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              {isSignUp && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading}>
                {loading ? "Loading..." : isSignUp ? "Create Account" : "Sign In"}
              </Button>
            </form>
              </>
            )}
          </CardContent>
          <div className="px-6 pb-6 pt-2 border-t">
            <div className="text-center">
              <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary">
                Privacy Policy
              </a>
            </div>
          </div>
        </Card>
      </div>
    </div>
    </>
  );
}
